import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  IconButton,
  Tooltip,
  Text,
  Divider,
  Collapse,
  useDisclosure,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  FormLabel,
  Select,
  Switch,
  FormLabel as ChakraFormLabel
} from '@chakra-ui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  AddIcon,
  DeleteIcon,
  ViewIcon,
  SettingsIcon,
  DownloadIcon,
  RepeatIcon,
  ArrowBackIcon,
  ArrowForwardIcon,
  SearchIcon,
  MinusIcon,
  SmallCloseIcon
} from '@chakra-ui/icons';

const ProjectSidebar = ({
  selectedTool,
  onToolChange,
  onDeleteSelected,
  onSave,
  onClear,
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onReset,
  onResetBottom,
  roomDimensions,
  onRoomDimensionsChange,
  wallLength,
  onWallLengthChange,
  windowLength,
  onWindowLengthChange,
  doorLength,
  onDoorLengthChange,
  selectedObject,
  isCollapsed,
  onToggleCollapse,
  draftObjectsCount = 0,
  onClearDrafts,
  onAddProduct
}) => {
  const { isOpen: isAdvancedOpen, onToggle: onAdvancedToggle } = useDisclosure();

  const tools = [
    { id: 'select', label: 'Select', icon: EditIcon, color: 'blue' },
    { id: 'room', label: 'Add Room', icon: AddIcon, color: 'green' },
    { id: 'wall', label: 'Add Wall', icon: AddIcon, color: 'orange' },
    { id: 'window', label: 'Add Window', icon: AddIcon, color: 'cyan' },
    { id: 'door', label: 'Add Door', icon: AddIcon, color: 'red' },
    { id: 'product', label: 'Add Product', icon: AddIcon, color: 'purple', action: onAddProduct },
  ];

  const actions = [
    { id: 'undo', label: 'Undo', icon: ArrowBackIcon, action: onUndo },
    { id: 'redo', label: 'Redo', icon: ArrowForwardIcon, action: onRedo },
    { id: 'delete', label: 'Delete', icon: DeleteIcon, action: onDeleteSelected, disabled: !selectedObject },
    { id: 'clear', label: 'Clear All', icon: SmallCloseIcon, action: onClear },
  ];

  const viewControls = [
    { id: 'zoomIn', label: 'Zoom In', icon: AddIcon, action: onZoomIn },
    { id: 'zoomOut', label: 'Zoom Out', icon: MinusIcon, action: onZoomOut },
    { id: 'reset', label: 'Reset View', icon: RepeatIcon, action: onReset },
    { id: 'resetBottom', label: 'Reset View Bottom', icon: RepeatIcon, action: onResetBottom },
  ];

  return (
    <Box
      position="fixed"
      left={0}
      top="120px"
      bottom={0}
      width={isCollapsed ? "50px" : "300px"}
      bg="white"
      borderRight="1px"
      borderColor="gray.200"
      transition="width 0.3s ease"
      zIndex={20}
      overflow="hidden"
    >

      {/* Toggle Button */}
      <Button
        leftIcon={isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        variant={'ghost'}
        colorScheme={'gray'}
        size="sm"
        width="100%"
        justifyContent="flex-start"
        onClick={onToggleCollapse}
      >
        {isCollapsed ? "" : "Collapse Sidebar"}
      </Button>

      <Collapse in={!isCollapsed} animateOpacity>
        <Box height="calc(100vh - 140px)" overflowY="auto">
          <VStack spacing={4} p={4} align="stretch">

          

          {/* Tools Section */}
          <Box>
            <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
              Drawing Tools
            </Text>
            <VStack spacing={2}>
              
              {tools.map((tool) => (
                <Button
                  key={tool.id}
                  leftIcon={<tool.icon />}
                  variant={selectedTool === tool.id ? 'solid' : 'ghost'}
                  colorScheme={selectedTool === tool.id ? tool.color : 'gray'}
                  size="sm"
                  width="100%"
                  justifyContent="flex-start"
                  onClick={() => {
                    if (tool.action) {
                      tool.action();
                    } else {
                      onToolChange(tool.id);
                    }
                  }}
                >
                  {tool.label}
                </Button>
              ))}
            </VStack>
          </Box>

          <Divider />

          {/* Actions Section */}
          <Box>
            <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
              Actions
            </Text>
            <VStack spacing={2}>
              {actions.map((action) => (
                <Button
                  key={action.id}
                  leftIcon={<action.icon />}
                  variant="ghost"
                  colorScheme="gray"
                  size="sm"
                  width="100%"
                  justifyContent="flex-start"
                  onClick={action.action}
                  isDisabled={action.disabled}
                >
                  {action.label}
                </Button>
              ))}
            </VStack>
          </Box>

          <Divider />

          {/* View Controls */}
          <Box>
            <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
              View Controls
            </Text>
            <VStack spacing={2}>
              {viewControls.map((control) => (
                <Button
                  key={control.id}
                  leftIcon={<control.icon />}
                  variant="ghost"
                  colorScheme="gray"
                  size="sm"
                  width="100%"
                  justifyContent="flex-start"
                  onClick={control.action}
                >
                  {control.label}
                </Button>
              ))}
            </VStack>
          </Box>

          <Divider />

          {/* Tool-specific Settings */}
          {selectedTool === 'room' && (
            <Box>
              <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
                Room Settings
              </Text>
              <VStack spacing={3}>
                <FormControl size="sm">
                  <FormLabel fontSize="xs">Width</FormLabel>
                  <NumberInput
                    value={roomDimensions.width}
                    onChange={(value) => onRoomDimensionsChange({ ...roomDimensions, width: parseInt(value) || 0 })}
                    min={50}
                    max={1000}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                
                <FormControl size="sm">
                  <FormLabel fontSize="xs">Height</FormLabel>
                  <NumberInput
                    value={roomDimensions.height}
                    onChange={(value) => onRoomDimensionsChange({ ...roomDimensions, height: parseInt(value) || 0 })}
                    min={50}
                    max={1000}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </VStack>
            </Box>
          )}

          {selectedTool === 'window' && (
            <Box>
              <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
                Window Settings
              </Text>
              <VStack spacing={3}>
                <FormControl size="sm">
                  <FormLabel fontSize="xs">Length</FormLabel>
                  <NumberInput
                    value={windowLength}
                    onChange={(value) => onWindowLengthChange(parseInt(value) || 0)}
                    min={20}
                    max={200}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <Text fontSize="xs" color="gray.500">
                  Width: 8px (fixed)
                </Text>
              </VStack>
            </Box>
          )}

          {selectedTool === 'door' && (
            <Box>
              <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
                Door Settings
              </Text>
              <VStack spacing={3}>
                <FormControl size="sm">
                  <FormLabel fontSize="xs">Length</FormLabel>
                  <NumberInput
                    value={doorLength}
                    onChange={(value) => onDoorLengthChange(parseInt(value) || 0)}
                    min={20}
                    max={200}
                    size="sm"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <Text fontSize="xs" color="gray.500">
                  Width: 8px (fixed)
                </Text>
              </VStack>
            </Box>
          )}

          <Divider />

          {/* Draft Objects Section */}
          {draftObjectsCount > 0 && (
            <Box>
              <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3}>
                Draft Objects ({draftObjectsCount})
              </Text>
              <VStack spacing={2}>
                <Button
                  leftIcon={<SmallCloseIcon />}
                  variant="ghost"
                  colorScheme="orange"
                  size="sm"
                  width="100%"
                  justifyContent="flex-start"
                  onClick={onClearDrafts}
                >
                  Clear All Drafts
                </Button>
              </VStack>
            </Box>
          )}

          <Divider />

          {/* Save Button */}
          <Button
            colorScheme="blue"
            size="sm"
            onClick={onSave}
            leftIcon={<DownloadIcon />}
          >
            Save Changes
          </Button>

          {/* Status */}
          <Box>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Current Tool: {selectedTool}
            </Text>
            {selectedObject && (
              <Text fontSize="xs" color="gray.500">
                Selected: {selectedObject.name || 'Object'}
              </Text>
            )}
          </Box>
        </VStack>
        </Box>
      </Collapse>

      {/* Collapsed View */}
      <Collapse in={isCollapsed} animateOpacity>
        <VStack spacing={2} p={2} align="center">
          {tools.map((tool) => (
            <Tooltip key={tool.id} label={tool.label} placement="right">
              <IconButton
                icon={<tool.icon />}
                variant={selectedTool === tool.id ? 'solid' : 'ghost'}
                colorScheme={selectedTool === tool.id ? tool.color : 'gray'}
                size="sm"
                onClick={() => onToolChange(tool.id)}
              />
            </Tooltip>
          ))}
          
          <Divider />
          
          <Tooltip label="Save" placement="right">
            <IconButton
              icon={<DownloadIcon />}
              colorScheme="blue"
              size="sm"
              onClick={onSave}
            />
          </Tooltip>
        </VStack>
      </Collapse>
    </Box>
  );
};

export default ProjectSidebar; 